/**
 * A small, consistent icon set drawn on a 20px grid at 1.6 stroke so the
 * navigation reads evenly. Inline rather than an icon package: nine glyphs
 * do not justify a dependency.
 */
type IconProps = { className?: string };

const base = (className = 'size-[18px]') => ({
  className,
  viewBox: '0 0 20 20',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
});

export const IconDashboard = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <rect x="2.5" y="2.5" width="6" height="7" rx="1.2" />
    <rect x="11.5" y="2.5" width="6" height="4" rx="1.2" />
    <rect x="11.5" y="9" width="6" height="8.5" rx="1.2" />
    <rect x="2.5" y="12" width="6" height="5.5" rx="1.2" />
  </svg>
);

export const IconUsers = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <circle cx="7.5" cy="6.5" r="2.8" />
    <path d="M2.5 16.5c0-2.8 2.2-4.6 5-4.6s5 1.8 5 4.6" />
    <path d="M13.5 4.2a2.8 2.8 0 0 1 0 5.3M15 11.7c1.6.5 2.5 1.9 2.5 3.6" />
  </svg>
);

export const IconCalendar = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <rect x="2.5" y="4" width="15" height="13.5" rx="1.6" />
    <path d="M2.5 8h15M6.5 2.5v3M13.5 2.5v3" />
  </svg>
);

export const IconShield = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M10 2.5 3.5 5v5c0 4 2.8 6.6 6.5 7.5 3.7-.9 6.5-3.5 6.5-7.5V5L10 2.5Z" />
    <path d="m7.5 9.8 1.8 1.8 3.4-3.6" />
  </svg>
);

export const IconEye = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M1.8 10S4.6 4.8 10 4.8 18.2 10 18.2 10 15.4 15.2 10 15.2 1.8 10 1.8 10Z" />
    <circle cx="10" cy="10" r="2.4" />
  </svg>
);

export const IconSearch = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <circle cx="8.8" cy="8.8" r="5.3" />
    <path d="m12.8 12.8 4 4" />
  </svg>
);

export const IconSun = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <circle cx="10" cy="10" r="3.4" />
    <path d="M10 1.8v2M10 16.2v2M1.8 10h2M16.2 10h2M4.2 4.2l1.4 1.4M14.4 14.4l1.4 1.4M15.8 4.2l-1.4 1.4M5.6 14.4l-1.4 1.4" />
  </svg>
);

export const IconMoon = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M16.5 11.8A7 7 0 0 1 8.2 3.5a7 7 0 1 0 8.3 8.3Z" />
  </svg>
);

export const IconLogout = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M12.5 14.2v1.8a1.5 1.5 0 0 1-1.5 1.5H4.5A1.5 1.5 0 0 1 3 16V4a1.5 1.5 0 0 1 1.5-1.5H11A1.5 1.5 0 0 1 12.5 4v1.8" />
    <path d="M7.5 10h9.5M14.2 7.2 17 10l-2.8 2.8" />
  </svg>
);

export const IconChevron = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="m7.5 4.5 5 5.5-5 5.5" />
  </svg>
);

export const IconMenu = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M3 5.5h14M3 10h14M3 14.5h14" />
  </svg>
);

export const IconMessage = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M17 11.5a2.5 2.5 0 0 1-2.5 2.5H7l-4 3V5.5A2.5 2.5 0 0 1 5.5 3h9A2.5 2.5 0 0 1 17 5.5Z" />
  </svg>
);

/** The trial: a first spark, before the commitment. */
export const IconSparkle = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M10 2.5 11.6 7 16 8.6 11.6 10.2 10 14.6 8.4 10.2 4 8.6 8.4 7Z" />
    <path d="M15.5 13.5 16.2 15.3 18 16l-1.8.7-.7 1.8-.7-1.8L13 16l1.8-.7Z" />
  </svg>
);

/** A bundle: several lessons bought as one thing. */
export const IconLayers = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M10 2.5 17.5 6.3 10 10.1 2.5 6.3Z" />
    <path d="m2.5 10 7.5 3.8 7.5-3.8" />
    <path d="m2.5 13.7 7.5 3.8 7.5-3.8" />
  </svg>
);

/** A recurring weekly time: the arrow comes back round. */
export const IconRepeat = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M14.5 3.5 16.5 5.5 14.5 7.5" />
    <path d="M3.5 9.5V8a2.5 2.5 0 0 1 2.5-2.5h10.5" />
    <path d="M5.5 16.5 3.5 14.5 5.5 12.5" />
    <path d="M16.5 10.5V12a2.5 2.5 0 0 1-2.5 2.5H3.5" />
  </svg>
);

/** One person handing over to another. */
export const IconSwap = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <circle cx="6" cy="6" r="2.5" />
    <path d="M2 15.5c.4-2.3 2-3.5 4-3.5s3.6 1.2 4 3.5" />
    <path d="M12.5 5.5h5m0 0-2-2m2 2-2 2" />
    <path d="M17.5 12.5h-5m0 0 2-2m-2 2 2 2" />
  </svg>
);

/** Something needs attention before it can go ahead. */
export const IconAlert = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M10 3 17.5 16.5h-15Z" />
    <path d="M10 8v3.5" />
    <path d="M10 14.2v.1" />
  </svg>
);
